import type { zhCN } from './zh-CN.ts';

/**
 * English dictionary — mirrors `zh-CN.ts`'s key space exactly via the
 * `Record<keyof typeof zhCN, string>` annotation. Removing or misnaming
 * a key here is a type error. Adding a new key: update `zh-CN.ts` first;
 * TS will then require the same key here.
 *
 * Value type is widened to `string` (not the literal-string type
 * `typeof zhCN` produces under `as const`) so each locale can carry its
 * own copy without the compiler demanding the Chinese phrase verbatim.
 * The key-space guarantee is the contract we need; the value is free-
 * form by design.
 *
 * Copy style
 * - Sentence-case for user-facing text (not Title Case), matching the
 *   existing console-web conventions in the pages before this PR.
 * - Use en-dash (`–`) for numeric ranges (e.g. `10–128 chars`) — same
 *   punctuation the pre-i18n sources used.
 * - Prefer imperative verbs in buttons ("Sign in", not "Please sign in").
 */
export const en: Record<keyof typeof zhCN, string> = {
  // ── <head> metadata ─────────────────────────────────────────────
  'meta.title': 'Nadoutong Console',
  'meta.description':
    'Nadoutong developer console: manage API keys, review usage, top up balance, and invite teammates.',

  // ── Common / shared ──────────────────────────────────────────────
  'common.brand.title': 'Nadoutong Console',
  'common.session.checking': 'Checking session…',
  'common.back_to_login': 'Back to sign-in',
  'common.technical_details.title': 'Technical details',
  'common.technical_details.show': 'Show',
  'common.technical_details.hide': 'Hide',

  // Locale-switcher native labels live in `types.ts` as
  // `LOCALE_NATIVE_LABELS` — see the canonical zh-CN dictionary for
  // the explanatory note.

  // ── Login ───────────────────────────────────────────────────────
  'login.eyebrow': 'Sign in',
  'login.subtitle': 'Sign in to your Nadoutong account',
  'login.description': 'Manage API keys, balance, usage, and teammates after you sign in.',
  'login.email': 'Email',
  'login.password': 'Password',
  'login.submit': 'Sign in',
  'login.submitting': 'Signing in…',
  'login.need_account': 'Need an account? Register',
  'login.forgot': 'Forgot password?',
  'login.error.empty_fields': 'Email and password are required',

  // ── Register ────────────────────────────────────────────────────
  'register.eyebrow': 'Create account',
  'register.subtitle': 'Create your Nadoutong account',
  'register.description': 'Start with your own account, then invite teammates when you are ready.',
  'register.email': 'Email',
  'register.password_with_hint': 'Password (10–128 chars)',
  'register.workspace_name': 'Team name (optional)',
  'register.workspace_placeholder': 'For example: Payments / Data Platform / My Team',
  'register.submit': 'Create account',
  'register.submitting': 'Creating account…',
  'register.error.empty_fields': 'Email and password are required',
  'register.error.password_too_short': 'Password must be at least 10 characters',
  'register.success.title': 'Check your inbox',
  'register.success.body':
    'We sent a verification link to {email}. Open the link in the email to continue into the console.',
  'register.success.resend_ack':
    'A new verification link is on its way. If it still does not arrive, check spam or wait a minute before trying again.',
  'register.success.resend_hint': 'If the email has not arrived yet, you can send another one.',
  'register.resend.submit': 'Resend link',
  'register.resend.submitting': 'Sending…',
  'register.already_have_account': 'Already have an account? Sign in',

  // ── Forgot password ─────────────────────────────────────────────
  'forgot.eyebrow': 'Recover access',
  'forgot.subtitle': 'Reset your sign-in password',
  'forgot.description': 'Enter your account email and we will send you a reset link.',
  'forgot.email': 'Email',
  'forgot.submit': 'Send reset link',
  'forgot.submitting': 'Sending…',
  'forgot.error.empty_email': 'Email is required',
  'forgot.success.title': 'Check your email',
  'forgot.success.body':
    'If an account exists for {email}, we sent a reset link. The link is valid for 24 hours.',

  // ── Reset password ──────────────────────────────────────────────
  'reset.eyebrow': 'New password',
  'reset.subtitle': 'Set a new password',
  'reset.description': 'Save the new password, then head back to sign in.',
  'reset.new_password': 'New password (10–128 chars)',
  'reset.confirm_password': 'Confirm new password',
  'reset.submit': 'Save password',
  'reset.submitting': 'Saving…',
  'reset.error.missing_token': 'Reset token missing from link',
  'reset.error.password_too_short': 'Password must be at least 10 characters',
  'reset.error.password_mismatch': 'Passwords do not match',
  'reset.error.token_banner':
    'Reset token missing — open the email link directly, or request a new one.',
  'reset.success.title': 'Password reset',
  'reset.success.body': 'Your password has been updated. Sign in with the new password.',
  'reset.success.sign_in': 'Sign in',

  // ── Verify email ────────────────────────────────────────────────
  'verify.eyebrow': 'Verify email',
  'verify.subtitle': 'Finish verifying your email',
  'verify.description':
    'Once verification succeeds, you will be taken into the console automatically.',
  'verify.missing_token':
    'This link is missing the verification token. Open the email link directly, or request a fresh one from the sign-in page.',
  'verify.pending': 'Verifying your email…',
  'verify.error.title': 'Verification failed',
  'verify.resend.label': 'Request a new link',
  'verify.resend.submit': 'Send a new link',
  'verify.resend.submitting': 'Sending…',
  'verify.resend.ack.title': 'Check your inbox',
  'verify.resend.ack.body':
    'If an account exists for that address and has not yet been verified, a fresh link is on its way.',

  // ── Invitation ──────────────────────────────────────────────────
  'invite.eyebrow': 'Team invitation',
  'invite.subtitle': 'Join a team',
  'invite.description': 'Review the team details first, then finish joining.',
  'invite.loading': 'Loading invitation…',
  'invite.error.missing_token': 'Missing invitation token in the URL',
  'invite.error.title': 'Invitation unavailable',
  'invite.invite_to': "You're invited to",
  'invite.role': 'Role',
  'invite.invitee_email': 'Invitee email',
  'invite.invited_by': 'Invited by',
  'invite.expires': 'Expires',
  'invite.password_with_hint': 'Password (10–128 chars)',
  'invite.display_name': 'Display name (optional)',
  'invite.display_name_placeholder': 'Defaults to email prefix',
  'invite.session_match': "You're signed in as {email}. Click accept to join {org}.",
  'invite.session_mismatch.body':
    "You're currently signed in as {current}, but this invitation is for {target}. Enter the password for that account to accept, or ",
  'invite.session_mismatch.switch_link': 'switch account',
  'invite.session_mismatch.suffix': '.',
  'invite.accept': 'Accept invitation',
  'invite.join': 'Join team',
  'invite.accepting': 'Accepting…',
  'invite.cancel': 'Cancel',
  'invite.error.password_too_short': 'Password must be at least {min} characters',
  'invite.error.password_too_long': 'Password must be at most {max} characters',
  'invite.toast.accepted': 'Invitation accepted',

  // ── Not found ──────────────────────────────────────────────────
  'notfound.title': 'Page not found',
  'notfound.body': 'The page you were looking for does not exist in this console.',
  'notfound.cta': 'Go to dashboard',

  // ── Shell chrome ───────────────────────────────────────────────
  'shell.brand.subtitle': 'API Console',
  'shell.brand.supporting': 'Manage your API keys, balance, usage, and teammates in one place.',
  'shell.nav.aria_label': 'Primary',
  'shell.nav.dashboard': 'Overview',
  'shell.nav.keys': 'Keys',
  'shell.nav.usage': 'Usage',
  'shell.nav.requests': 'Request logs',
  'shell.nav.pricing': 'Model pricing',
  'shell.nav.billing': 'Balance & billing',
  'shell.nav.settings': 'Account & team',
  'shell.org_chip.title': 'Current team',
  'shell.page.console_fallback': 'Console',
  'shell.session.active': 'Current session',
  'shell.session.verified': 'Email verified',
  'shell.session.unverified': 'Email verification pending',
  'shell.session.signed_in': 'signed in',
  'shell.logout': 'Log out',

  // ── Dashboard ──────────────────────────────────────────────────
  'dashboard.title': 'Overview',
  'dashboard.subtitle': 'Quickly check your balance, usage, and recent changes.',
  'dashboard.stat.balance.label': 'Available balance',
  'dashboard.stat.balance.updated': 'Updated {timestamp}',
  'dashboard.stat.balance.unavailable': 'Balance unavailable',
  'dashboard.stat.balance.loading': 'Loading…',
  'dashboard.stat.balance.zero_cta': 'Your balance is empty — top up now',
  'dashboard.stat.cost.label': 'This month',
  'dashboard.stat.cost.tokens': '{tokens} tokens used this month',
  'dashboard.stat.cost.unavailable': 'Usage unavailable',
  'dashboard.stat.cost.loading': 'Loading…',
  'dashboard.stat.keys.label': 'Available keys',
  'dashboard.stat.keys.description': 'Keys that can still be used right now',
  'dashboard.stat.keys.unavailable': 'Keys unavailable',
  'dashboard.stat.keys.empty_cta': 'No usable keys yet — create your first one',
  'dashboard.stat.last_request.label': 'Last request',
  'dashboard.stat.last_request.empty': 'No requests yet',
  'dashboard.stat.last_request.hint': 'Updates automatically when new requests arrive',
  'dashboard.stat.last_request.empty_cta': 'Create a key to start sending requests',
  'dashboard.activity.title': 'Recent requests',
  'dashboard.activity.view_all': 'View all usage',
  'dashboard.activity.loading': 'Loading recent activity…',
  'dashboard.activity.error': 'Could not load recent activity. Reload to retry.',
  'dashboard.activity.empty': 'No requests in the last 7 days yet.',
  'dashboard.activity.empty_cta': 'Create a key, then send your first request',
  'dashboard.activity.column.hour': 'Hour',
  'dashboard.activity.column.requests': 'Requests',
  'dashboard.activity.column.tokens': 'Token volume',
  'dashboard.activity.column.cost': 'Cost',
  'dashboard.top_keys.title': 'Most-used keys this month',
  'dashboard.top_keys.manage': 'Manage keys',
  'dashboard.top_keys.loading': 'Loading key usage…',
  'dashboard.top_keys.error': 'Could not load key usage.',
  'dashboard.top_keys.empty.chip': 'no usage yet',
  'dashboard.top_keys.empty.hint': 'Keys will show up here once they start handling requests.',
  'dashboard.top_keys.empty_cta': 'Create a key',
  'dashboard.top_keys.column.key': 'Key',
  'dashboard.top_keys.column.requests': 'Requests',
  'dashboard.top_keys.column.cost': 'Cost',

  // ── API keys page ──────────────────────────────────────────────
  'keys.title': 'API keys',
  'keys.subtitle': 'Create and manage the keys your applications use to call Nadoutong.',
  'keys.create': 'Create key',
  'keys.base_url.title': 'Connection URL',
  'keys.base_url.description': 'Use this base URL in your SDK, client, or server configuration.',
  'keys.loading': 'Loading keys…',
  'keys.error': 'Could not load keys',
  'keys.empty': 'No keys yet — create one to start making requests.',
  'keys.copied': 'Copied',
  'keys.click_to_copy': 'Click to copy',
  'keys.show_revoked': 'Show revoked ({count})',
  'keys.column.prefix': 'Prefix',
  'keys.column.name': 'Name',
  'keys.column.vendor': 'Vendor',
  'keys.column.channel': 'Channel',
  'keys.column.rate': 'Rate',
  'keys.column.created': 'Created',
  'keys.column.last_used': 'Last used',
  'keys.column.status': 'Status',
  'keys.column.actions': 'Actions',
  'keys.status.revoked': 'Disabled',
  'keys.status.active': 'Usable',
  'keys.never_used': 'Never',
  'keys.save': 'Save',
  'keys.cancel': 'Cancel',
  'keys.rename': 'Rename',
  'keys.revoke': 'Delete',
  'keys.row_error.name_required': 'Name cannot be empty',
  'keys.row_error.network': 'Network error',
  'keys.create_modal.title': 'Create API key',
  'keys.create_modal.description':
    'Give this key a name you can recognize later. The full key is only shown once after creation, so save it right away.',
  'keys.create_modal.name_label': 'Key name (optional)',
  'keys.create_modal.name_placeholder': 'production-backend',
  'keys.create_modal.routing_summary':
    'Every key must choose a provider and channel for routing, model availability, and pricing.',
  'keys.create_modal.vendor_label': 'Provider (required)',
  'keys.create_modal.vendor_placeholder': 'Choose provider',
  'keys.create_modal.channel_label': 'Channel (required)',
  'keys.create_modal.channel_placeholder': 'Choose channel',
  'keys.create_modal.channel_required': 'Choose a provider and channel before creating the key.',
  'keys.create_modal.channels_error': 'Could not load available channels. Try again later.',
  'keys.create_modal.channels_empty': 'No channels are available. Contact support.',
  'keys.create_modal.catalog_error':
    'Model coverage could not be loaded. You can still create the key, but check /v1/models after creation.',
  'keys.create_modal.catalog_empty': 'This channel has no published model coverage right now.',
  'keys.create_modal.submit': 'Create key',
  'keys.create_modal.submitting': 'Creating…',
  'keys.create_modal.cancel': 'Cancel',
  'keys.technical.summary': 'View the provider, channel, and pricing details behind this key.',
  'keys.technical.vendor': 'Provider',
  'keys.technical.channel': 'Technical channel',
  'keys.technical.rate': 'Pricing multiplier',
  'keys.technical.default': 'Default',
  'keys.plaintext.title': 'Copy your API key',
  'keys.plaintext.warning':
    'This is the only time you can see the full key. Copy it now and store it safely; if you lose it, you will need to delete it and create a new one.',
  'keys.plaintext.hint.click_to_copy': 'Click to copy',
  'keys.plaintext.hint.click_with_name': '{name} · click to copy',
  'keys.plaintext.copied': 'Copied to clipboard',
  'keys.plaintext.copy_anywhere': 'Click anywhere on this block to copy',
  'keys.plaintext.done': 'I have copied the key',
  'keys.plaintext.copy_aria_label': 'Copy API key to clipboard',
  'keys.models.count': '{count} models',
  'keys.revoke_modal.title': 'Delete this API key?',
  'keys.revoke_modal.description.before': 'Deleting the key ',
  'keys.revoke_modal.description.after':
    ' immediately invalidates it. Any application still using it will start receiving 401 errors. This cannot be undone.',
  'keys.revoke_modal.submit': 'Delete',
  'keys.revoke_modal.submitting': 'Deleting…',
  'keys.revoke_modal.cancel': 'Cancel',

  // ── Usage page ─────────────────────────────────────────────────
  'usage.title': 'Usage',
  'usage.subtitle': 'Track request volume, token usage, and cost over time.',
  'usage.range.aria_label': 'Time range',
  'usage.range.24h': '24h',
  'usage.range.7d': '7d',
  'usage.range.30d': '30d',
  'usage.daily.title': 'Daily tokens · {label}',
  'usage.daily.subtitle': 'Helps you spot broader token trends over time.',
  'usage.daily.loading': 'Loading daily usage…',
  'usage.daily.error': 'Could not load daily usage',
  'usage.daily.empty': 'No usage in the last {label}',
  'usage.legend.input': 'Input tokens',
  'usage.legend.output': 'Output tokens',
  'usage.summary.requests': 'Requests',
  'usage.summary.input_tokens': 'Input tokens',
  'usage.summary.output_tokens': 'Output tokens',
  'usage.summary.cost': 'Cost',
  'usage.summary.range': 'Range: {label}',
  'usage.summary.loading': 'Loading…',
  'usage.summary.cost_unit': 'Shown in USD',
  'usage.summary.cache_read_suffix': '+ {tokens} cached input tokens',
  'usage.summary.cache_write_suffix': '+ {tokens} cached write tokens',
  'usage.technical.summary': 'View the extra token volume caused by cache hits and cache writes.',
  'usage.technical.cache_read': 'Cache-hit tokens',
  'usage.technical.cache_write': 'Cache-write tokens',
  'usage.chart.title': 'Request trend · {label}',
  'usage.chart.loading': 'Loading chart…',
  'usage.chart.error': 'Could not load chart',
  'usage.by_key.title': 'By key · {label}',
  'usage.by_key.loading': 'Loading breakdown…',
  'usage.by_key.error': 'Could not load breakdown',
  'usage.by_key.empty': 'No usage in this range.',
  'usage.by_key.unnamed_key': 'Unnamed key',
  'usage.by_key.unknown_key': 'Unknown key',
  'usage.by_key.column.key_name': 'Key name',
  'usage.by_key.column.requests': 'Requests',
  'usage.by_key.column.input_tokens': 'Input tokens',
  'usage.by_key.column.output_tokens': 'Output tokens',
  'usage.by_key.column.cache_read': 'Cache hit',
  'usage.by_key.column.cache_write': 'Cache write',
  'usage.by_key.column.cost': 'Cost',

  // ── Request logs page ─────────────────────────────────────────
  'requests.title': 'Request logs',
  'requests.subtitle': 'Inspect each request by status, model, tokens, cache, and cost.',
  'requests.range.aria_label': 'Request log time range',
  'requests.range.24h': '24h',
  'requests.range.7d': '7d',
  'requests.range.30d': '30d',
  'requests.filter.request_id': 'Request ID',
  'requests.filter.request_id_placeholder': 'Exact request id',
  'requests.filter.key': 'Key',
  'requests.filter.key_all': 'All keys',
  'requests.filter.status': 'Status',
  'requests.filter.status_all': 'All statuses',
  'requests.filter.status_success': 'Success',
  'requests.filter.status_error': 'Error',
  'requests.filter.submit': 'Search',
  'requests.filter.clear': 'Clear',
  'requests.key.unnamed': 'Unnamed key',
  'requests.key.unknown': 'Unknown key',
  'requests.loading': 'Loading request logs…',
  'requests.error': 'Could not load request logs.',
  'requests.empty': 'No requests match these filters.',
  'requests.column.time': 'Time',
  'requests.column.request': 'Request ID',
  'requests.column.key': 'Key name',
  'requests.column.model': 'Model',
  'requests.column.status': 'Status',
  'requests.column.duration': 'Duration',
  'requests.column.input': 'Input',
  'requests.column.output': 'Output',
  'requests.column.cache_read': 'Cache hit',
  'requests.column.cache_write': 'Cache write',
  'requests.column.cost': 'Cost',
  'requests.duration.ms': '{value} ms',
  'requests.detail.request_id': 'Full request ID',
  'requests.detail.api_key_id': 'Key ID',
  'requests.detail.protocol': 'Protocol',
  'requests.pagination.summary': '{from}–{to} / {total} total',
  'requests.pagination.prev': 'Newer',
  'requests.pagination.next': 'Older',

  // ── Pricing page ──────────────────────────────────────────────
  'pricing.title': 'Model pricing',
  'pricing.subtitle': 'Compare effective per-million-token prices by provider, channel, and model.',
  'pricing.unit': 'per 1M tokens · USD',
  'pricing.loading': 'Loading model pricing…',
  'pricing.error': 'Could not load model pricing.',
  'pricing.empty': 'No model pricing is ready to display yet.',
  'pricing.vendor.summary': '{models} models · {channels} channels',
  'pricing.vendor.no_channels': 'This provider has no available channels right now.',
  'pricing.column.model': 'Model',
  'pricing.cell.unavailable': 'Unavailable',
  'pricing.rate.input': 'Input',
  'pricing.rate.output': 'Output',
  'pricing.rate.cache_read': 'Cache hit',
  'pricing.rate.cache_write': 'Cache write',

  // ── Billing page ───────────────────────────────────────────────
  'billing.title': 'Balance & billing',
  'billing.subtitle': 'Check your balance, top-ups, credits, and ledger history.',
  'billing.balance.title': 'Current balance',
  'billing.balance.status.funded': 'Balance available',
  'billing.balance.status.zero': 'Needs top-up',
  'billing.balance.loading': 'Loading balance…',
  'billing.balance.error': 'Could not load balance.',
  'billing.balance.updated': 'Updated {timestamp}',
  'billing.topup.entry': 'Top up',
  'billing.redeem.title': 'Redeem gift card',
  'billing.redeem.code_label': 'Gift-card code',
  'billing.redeem.code_placeholder': '32-character hex code',
  'billing.redeem.submit': 'Redeem',
  'billing.redeem.submitting': 'Redeeming…',
  'billing.redeem.error.code_required': 'Gift-card code is required',
  'billing.redeem.toast.success': 'Credited {amount}, balance {balance}',
  'billing.transactions.title': 'Balance history',
  'billing.transactions.latest': 'Latest',
  'billing.transactions.offset': 'Starting from item {offset}',
  'billing.transactions.page': 'Page {page}',
  'billing.transactions.loading': 'Loading transactions…',
  'billing.transactions.error': 'Could not load transactions.',
  'billing.transactions.empty': 'No transactions yet.',
  'billing.transactions.column.date': 'Date',
  'billing.transactions.column.type': 'Type',
  'billing.transactions.column.amount_usd': 'Amount (USD)',
  'billing.transactions.column.running': 'Balance',
  'billing.transactions.newer': 'Newer',
  'billing.transactions.older': 'Older',
  'billing.transactions.type.charge': 'Request charges',
  'billing.transactions.type.gift_card_redeem': 'Gift card credit',
  'billing.transactions.type.ops_adjust': 'Manual adjustment',
  'billing.transactions.type.topup': 'Top-up credit',
  'billing.pricing.title': 'Model pricing',
  'billing.pricing.unit': 'per 1M tokens · USD',
  'billing.pricing.loading': 'Loading pricing…',
  'billing.pricing.error': 'Could not load pricing.',
  'billing.pricing.empty': 'No model pricing is ready to display yet.',
  'billing.pricing.column.vendor': 'Provider',
  'billing.pricing.column.model': 'Model',
  'billing.pricing.column.input': 'Input',
  'billing.pricing.column.output': 'Output',
  'billing.pricing.column.cache_read': 'Cache hit',
  'billing.pricing.column.cache_write': 'Cache write',
  'billing.pricing.column.effective_from': 'Effective from',

  // ── Billing topup page ─────────────────────────────────────────
  'topup.title': 'Top up',
  'topup.subtitle':
    'Fund your balance with WeChat Pay. The next step shows a QR code you can scan with your phone.',
  'topup.amount.label': 'Amount (CNY · whole yuan)',
  'topup.amount.hint': 'CNY · min {min} · max {max} · whole yuan only',
  'topup.usd.label': 'Balance you will receive',
  'topup.usd.hint':
    'Estimated at about {rate} ¥/$. The final credited amount uses the rate locked in when the order is created.',
  'topup.usd.hint_loading': 'Loading the current server quote.',
  'topup.usd.hint_unavailable':
    'Quote unavailable. The final credited amount will be locked by the server when the order is created.',
  'topup.pay_type.legend': 'Payment method',
  'topup.pay_type.alipay': 'Alipay',
  'topup.pay_type.wxpay': 'WeChat Pay',
  'topup.error.whole_yuan': 'Enter a whole-yuan amount (integer, no decimals).',
  'topup.error.positive': 'Enter a positive whole-yuan amount.',
  'topup.error.required': 'Enter an amount to continue.',
  'topup.error.no_cents': 'Amount must be whole yuan (no fen).',
  'topup.error.min': 'Minimum top-up is {amount}.',
  'topup.error.max': 'Maximum top-up is {amount}.',
  'topup.error.no_redirect': 'Payment channel did not return a redirect URL.',
  'topup.cancel': 'Cancel',
  'topup.submit': 'Continue to pay →',
  'topup.submitting': 'Creating…',
  'topup.fee_hint':
    'Exchange rates refresh regularly, and Nadoutong covers the payment-channel fee.',
  'topup.qr.title': 'Scan to pay',
  'topup.qr.order_prefix': 'Order ',
  'topup.qr.image_alt': 'Payment QR code',
  'topup.qr.fallback_hint': 'Open WeChat on your phone and scan the target URL below.',
  'topup.qr.status.paid': 'Payment received. Redirecting…',
  'topup.qr.status.other': 'Order {status}',
  'topup.qr.status.pending': 'Waiting for payment…',
  'topup.qr.status.slow': 'Taking longer than expected…',
  'topup.qr.slow_notice':
    "We've stopped polling to save bandwidth. If you already paid in the app, your balance will update on its own — it's safe to close this.",
  'topup.qr.close': 'Close',

  // ── Settings page ──────────────────────────────────────────────
  'settings.title': 'Account & team',
  'settings.subtitle': 'Manage your account, teammates, and security settings.',
  'settings.account.title': 'Account details',
  'settings.account.description': 'This section shows the information you use most often.',
  'settings.account.email': 'Email',
  'settings.account.account_id': 'Account id',
  'settings.account.org_id': 'Team id',
  'settings.account.email_verified': 'Email verified',
  'settings.account.status.verified': 'verified',
  'settings.account.status.unverified': 'unverified',
  'settings.technical.summary':
    'Raw IDs and other technical fields live here for troubleshooting when you need them.',
  'settings.members.title': 'Team members',
  'settings.members.subtitle':
    'Everyone who can access this team. Owners and admins can invite new members.',
  'settings.members.invite_button': 'Invite member',
  'settings.members.loading': 'Loading members…',
  'settings.members.error': 'Could not load members',
  'settings.members.empty': 'No members yet.',
  'settings.members.column.email': 'Email',
  'settings.members.column.role': 'Role',
  'settings.members.column.joined': 'Joined',
  'settings.members.role.owner': 'Owner',
  'settings.members.role.admin': 'Admin',
  'settings.members.role.member': 'Member',
  'settings.invitations.title': 'Pending invitations',
  'settings.invitations.loading': 'Loading…',
  'settings.invitations.error': 'Could not load invitations',
  'settings.invitations.empty': 'No pending invitations.',
  'settings.invitations.column.email': 'Email',
  'settings.invitations.column.role': 'Role',
  'settings.invitations.column.invited_by': 'Invited by',
  'settings.invitations.column.expires': 'Expires',
  'settings.invitations.column.actions': 'Actions',
  'settings.invitations.revoke': 'Revoke',
  'settings.invite_modal.title': 'Invite a member',
  'settings.invite_modal.description':
    'Invitees receive an email, and you can also copy the invite link to share it yourself.',
  'settings.invite_modal.email': 'Email',
  'settings.invite_modal.role': 'Role',
  'settings.invite_modal.role.member': 'Member',
  'settings.invite_modal.role.admin': 'Admin',
  'settings.invite_modal.email_required': 'Email is required',
  'settings.invite_modal.cancel': 'Cancel',
  'settings.invite_modal.submit': 'Send invitation',
  'settings.invite_modal.submitting': 'Sending…',
  'settings.invite_modal.toast.sent': 'Invitation sent',
  'settings.invite_link.title': 'Invitation sent',
  'settings.invite_link.description':
    'The recipient will get an email with the link below. You can also copy it and share it manually.',
  'settings.invite_link.click_to_copy': 'Click to copy',
  'settings.invite_link.copied': 'Copied to clipboard',
  'settings.invite_link.copy_anywhere': 'Click anywhere on this block to copy',
  'settings.invite_link.done': 'Done',
  'settings.invite_link.aria_label': 'Copy invitation link to clipboard',
  'settings.security.title': 'Security',
  'settings.security.change_password': 'Change sign-in password',
  'settings.security.current_password': 'Current password',
  'settings.security.new_password': 'New password (10–128 chars)',
  'settings.security.confirm_password': 'Confirm new password',
  'settings.security.submit': 'Change password',
  'settings.security.submitting': 'Saving…',
  'settings.security.error.current_required': 'Current password is required',
  'settings.security.error.current_incorrect': 'Current password is incorrect',
  'settings.security.error.password_too_short': 'Password must be at least {min} characters',
  'settings.security.error.password_too_long': 'Password must be at most {max} characters',
  'settings.security.error.mismatch': 'Passwords do not match',
  'settings.security.error.network': 'Network error — try again',
  'settings.security.toast.success': 'Password changed',

  // ── i18n switcher chrome ───────────────────────────────────────
  'i18n.switcher.aria_label': 'Language',

  // ── Toast ───────────────────────────────────────────────────────
  'toast.region_aria': 'Notifications',
  'toast.dismiss_aria': 'Dismiss notification',

  // ── Billing topup return ────────────────────────────────────────
  'billing.topup_return.page.title': 'Topup result',
  'billing.topup_return.page.order_label': 'Order ',
  'billing.topup_return.page.waiting_order_id': 'Waiting for order id…',
  'billing.topup_return.loading.aria': 'Loading order status…',
  'billing.topup_return.missing.title': 'Missing order id',
  'billing.topup_return.missing.body':
    "This page is normally reached from the payment provider's return link. If you landed here by mistake, go back to the billing overview.",
  'billing.topup_return.missing.cta': 'Return to billing',
  'billing.topup_return.pending.title': 'Payment in progress',
  'billing.topup_return.pending.body':
    'Payment is being processed. This page will update automatically once the provider confirms — no need to reload.',
  'billing.topup_return.slow.title': 'Payment takes longer than usual',
  'billing.topup_return.slow.body':
    "The provider hasn't confirmed your payment yet. If you already completed payment in the app, the balance will update within a few minutes — you can safely close this tab.",
  'billing.topup_return.slow.reference': 'Reference order id:',
  'billing.topup_return.slow.cta.back': 'Back to billing',
  'billing.topup_return.slow.cta.support': 'Contact support',
  'billing.topup_return.paid.title': 'Payment received',
  'billing.topup_return.paid.credited': 'Credited',
  'billing.topup_return.paid.charged': 'Charged',
  'billing.topup_return.paid.at_rate': 'at {rate} ¥/$',
  'billing.topup_return.paid.new_balance': 'New balance',
  'billing.topup_return.paid.balance_loading': 'Loading…',
  'billing.topup_return.paid.redirect_hint':
    'Redirecting to the billing overview in a few seconds…',
  'billing.topup_return.paid.cta': 'Return to dashboard',
  'billing.topup_return.terminal.expired.title': 'Order expired',
  'billing.topup_return.terminal.expired.body':
    'This order expired before payment was completed. Start a new topup if you still want to add balance.',
  'billing.topup_return.terminal.failed.title': 'Payment failed',
  'billing.topup_return.terminal.failed.body':
    'The payment provider reported a failure for this order. No funds were charged — you can safely try again.',
  'billing.topup_return.terminal.refunded.title': 'Order refunded',
  'billing.topup_return.terminal.refunded.body':
    'This order was refunded. Start a new topup if you want to re-credit your balance.',
  'billing.topup_return.terminal.cta.retry': 'Try again',
  'billing.topup_return.terminal.cta.back': 'Back to billing',

  // ── Usage charts ────────────────────────────────────────────────
  'usage_chart.empty': 'No usage data for this range',
  'usage_chart.hourly.aria': 'Hourly usage — {count} buckets, peak {peak} requests',
  'usage_chart.hourly.tooltip': '{hour} — {requests} requests, {tokens} tokens',
  'usage_chart.daily.aria':
    'Daily token usage — {days} days, {total} total tokens ({input} input, {output} output)',
  'usage_chart.daily.tooltip':
    '{date} — {total} tokens ({input} input + {output} output), {requests} requests',

  // ── Skeleton ────────────────────────────────────────────────────
  'skeleton.loading': 'Loading…',

  // ── Error banner ────────────────────────────────────────────────
  'error_banner.unexpected': 'Unexpected error',
};
